import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, ChevronDown, X } from "lucide-react";
import { MADA_CITIES, filterCities } from "../data/madaCities";

/**
 * Sélecteur de ville Madagascar avec :
 * - Liste des 120+ villes principales (recherche en temps réel)
 * - Champ libre "Autre" pour saisir un village non listé
 *
 * Props :
 *   value: { name } | string | null
 *   onChange: (city: { name, lat, lng } | { name, lat: null, lng: null }) => void
 *   placeholder: string
 *   testId: string
 */
export const CitySelector = ({ value, onChange, placeholder = "Choisis ta ville…", testId = "city-selector" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const ref = useRef(null);

  const currentName = typeof value === "string" ? value : value?.name || "";

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => filterCities(query), [query]);

  const pick = (city) => {
    onChange({ name: city.name, lat: city.lat, lng: city.lng });
    setOpen(false);
    setQuery("");
    setCustomMode(false);
  };

  const pickCustom = () => {
    const v = query.trim();
    if (!v) return;
    onChange({ name: v, lat: null, lng: null });
    setOpen(false);
    setQuery("");
    setCustomMode(false);
  };

  const clear = () => {
    onChange(null);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full pl-9 pr-9 py-2.5 bg-white/[0.04] border border-white/10 hover:border-white/25 rounded-lg text-sm focus:border-yellow-400 focus:outline-none text-left flex items-center"
        data-testid={`${testId}-trigger`}
      >
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <span className={`truncate flex-1 ${currentName ? "text-white" : "text-white/45"}`}>
          {currentName || placeholder}
        </span>
        {currentName ? (
          <X size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-red-400" onClick={(e) => { e.stopPropagation(); clear(); }} />
        ) : (
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/35" />
        )}
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-zinc-950 border border-white/15 rounded-lg shadow-2xl shadow-black/60 max-h-80 overflow-hidden z-40">
          <div className="p-2 border-b border-white/10 sticky top-0 bg-zinc-950">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher (Tana, Tamatave, Tuléar…)"
              className="w-full px-3 py-2 bg-white/[0.05] border border-white/10 rounded text-sm focus:border-yellow-400 focus:outline-none"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto max-h-56" data-testid={`${testId}-list`}>
            {results.length === 0 && !customMode && (
              <div className="p-4 text-center text-sm text-white/55">
                Aucune ville trouvée.
                {query.trim() && (
                  <button type="button" onClick={pickCustom} className="block mt-2 mx-auto px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest rounded-full transition" data-testid={`${testId}-use-custom`}>
                    Utiliser "{query.trim()}"
                  </button>
                )}
              </div>
            )}
            {results.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => pick(c)}
                className="w-full text-left px-3 py-2 hover:bg-yellow-400/10 transition border-b border-white/5 last:border-0"
                data-testid={`${testId}-option-${c.name.toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`}
              >
                <p className="text-sm font-bold">{c.name}</p>
                <p className="text-[10px] text-white/45">{c.region}</p>
              </button>
            ))}
            {results.length > 0 && query.trim() && !results.some((r) => r.name.toLowerCase() === query.trim().toLowerCase()) && (
              <button type="button" onClick={pickCustom} className="w-full text-left px-3 py-2.5 bg-yellow-400/5 hover:bg-yellow-400/15 border-t border-yellow-400/20 transition" data-testid={`${testId}-use-custom-bottom`}>
                <p className="text-xs text-yellow-300 font-bold">+ Utiliser "{query.trim()}" comme ville</p>
                <p className="text-[10px] text-white/45">Pour les villages/quartiers non listés</p>
              </button>
            )}
          </div>
          <div className="px-3 py-1.5 text-[10px] text-white/40 bg-black/50 border-t border-white/5">
            {MADA_CITIES.length} villes de Madagascar
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySelector;
